import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";

/** "Ask the Wellness Coach" — supportive, non-medical AI companion for
 * patient lifestyle questions. Backed by /api/wellness/coach (Claude Sonnet
 * 4.6 via the Emergent Universal LLM key). Anything clinical is deflected
 * to the care team by the backend system prompt.
 *
 * Access
 * ──────
 * Subscribers or lifetime-code holders only. Non-paid patients see a
 * lock ribbon + a "Subscribers only" tease that links out to Pricing.
 */
const SAMPLE_PROMPTS = [
  "How do I stay motivated on tough weeks?",
  "Give me a low-effort dinner idea after a long day.",
  "What's a small habit that helps me sleep better?",
];

export default function WellnessCoachCard() {
  const [prompt, setPrompt] = useState("");
  const [reply, setReply] = useState("");
  const [easterEgg, setEasterEgg] = useState(null);
  const [gated, setGated] = useState(false);
  const [loading, setLoading] = useState(false);

  // Detect subscription status via a low-cost probe.
  useEffect(() => {
    (async () => {
      try {
        await API.post("/wellness/coach", { prompt: "hi" });
        setGated(false);
      } catch (e) {
        if (e?.response?.status === 402) setGated(true);
      }
    })();
  }, []);

  const ask = async (q) => {
    const text = (q ?? prompt).trim();
    if (text.length < 3) return;
    setLoading(true);
    setReply("");
    setEasterEgg(null);
    try {
      const { data } = await API.post("/wellness/coach", { prompt: text });
      setReply(data?.reply || "");
      setPrompt(text);
      if (data?.easter_egg && (data?.points_awarded || 0) > 0) {
        setEasterEgg({ points: data.points_awarded });
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setGated(true);
        toast.info("The Wellness Coach is included with a GLPFlo subscription.");
      } else {
        const msg = e?.response?.data?.detail || "Coach is taking a quick break — try again in a moment.";
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="wellness-coach-card" className="border-purple-200 dark:border-purple-900/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Ask the Wellness Coach
          {gated && (
            <span
              data-testid="coach-gate-ribbon"
              className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300"
            >
              <Lock className="w-3 h-3" />
              Subscribers only
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {gated
            ? "The AI coach is part of your GLPFlo subscription. Upgrade or redeem a lifetime code to unlock it."
            : "A friendly AI for lifestyle, motivation, and mindset questions. For anything medical, we'll route you to your care team."}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="flex gap-2"
        >
          <Input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={gated ? "Unlock to ask the coach…" : "Ask a wellness question…"}
            maxLength={300}
            disabled={loading || gated}
            data-testid="coach-input"
            className="rounded-full"
          />
          <Button
            type="submit"
            disabled={loading || gated || prompt.trim().length < 3}
            data-testid="coach-submit"
            className="rounded-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>

        {gated && (
          <Link
            to="/medications"
            data-testid="coach-unlock-btn"
            className="block text-center rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm py-2"
          >
            Unlock the Coach
          </Link>
        )}

        {!gated && !reply && !loading && (
          <div className="flex flex-wrap gap-1.5" data-testid="coach-suggestions">
            {SAMPLE_PROMPTS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-950/40"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="text-xs text-slate-400 dark:text-slate-500 italic" data-testid="coach-loading">Coach is thinking…</div>
        )}

        {reply && !loading && (
          <div
            className={`rounded-2xl border p-3 text-sm whitespace-pre-wrap leading-relaxed ${
              easterEgg
                ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/60 dark:text-emerald-100"
                : "bg-purple-50 border-purple-100 text-slate-800 dark:bg-purple-950/30 dark:border-purple-900/60 dark:text-slate-100"
            }`}
            data-testid="coach-reply"
          >
            {easterEgg && (
              <div className="flex items-center gap-1.5 mb-2 text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                <Gift className="w-3.5 h-3.5" />
                +{easterEgg.points} points awarded
              </div>
            )}
            {reply}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
